import * as PIXI from 'pixi.js';
import { Scene } from "pixi-scenegraph";
import { createMenuItem, createPanel, createPopupPanel, createText } from '@/ui/menu-builder';
import { HEIGHT, WIDTH } from "@/constants";
import { EVENT_BUS } from "@/model/event-bus";
import { Midi } from '@/input/midi';
import { PixiPiano } from "@/components/piano-component";
import { bounce } from '@/util/tween';
import { initVirtualKeyboard, VIRTUAL_KEYBOARD_SYMBOLS } from "@/input/keyboard";
import PianoUtil from '@/util/piano-util';
import { store } from '@/model/store';
import game from '@/model/game';

export class MenuDemoScene extends Scene {
    activeItem = 0;
    minKey = 40
    maxKey = 63

    constructor(sceneName = 'menu-demo') {
        super(sceneName); // this is the scene name
        this.buttons = [];
        this.initBackground();
        this.initStaticText();
        this.initButtons();
        // this.initKeyHandlers();
        if (Midi.meta.name) {
            this.createMidiSection();
        } else {
            this.createVirtualPianoSection();
        }
    }

    initStaticText() {
        const staticText = createText({
            text: 'Lets get started!',
            fontSize: 40
        });
        staticText.position.set(WIDTH / 2, HEIGHT * 0.1)
        this.addChild(staticText)
    }

    initBackground() {
        const background = PIXI.Sprite.from('public/images/background.jpeg');
        background.width = WIDTH; background.height = HEIGHT;
        this.addChild(background);
    }

    initPanel() {
        const panel = createPanel({
            x: 40 + 150,
            y: 310,
            headerText: 'Piano Rocker DEMO'
        });
        this.addChild(panel);

    }

    initButtons() {
        const item1 = createMenuItem({
            text: 'Continue', 
            yCoord: HEIGHT * 0.8,
            xCoord: WIDTH / 2 - 120,
            clickActionName: 'NAVIGATE',
            clickActionPayload: {
                sceneName: 'select-song'
            }
        });

        const item2 = createMenuItem({
            text: 'Refresh', 
            yCoord: HEIGHT * 0.8,
            xCoord: WIDTH / 2 + 120,
            clickActionName: 'REFRESH'
        });
        this.addChild(item1);
        this.addChild(item2);
        this.buttons = [item1, item2];
    }

    createMidiSection() {
        const LABEL = 'Your Midi device was recognized as: \n';
        const MESSAGE = `Now please press left- and right-most keys of your piano,\nto set-up the max range.\nCurrent range is: ${PianoUtil.note(this.minKey).toUpperCase()}...${PianoUtil.note(this.maxKey).toUpperCase()} (${this.maxKey - this.minKey} keys)\n\nOnce keyboard below looks like your press "Continue".\n"Refresh" will start it all over, if something didn't go right`
        function formatMeta(meta) {
            return LABEL + `${meta.manufacturer}-${meta.name} (${meta.state === 'connected' ? '✅' : '⛔'})\n\n` + MESSAGE;
        }
        const midiDeviceName = createText({
            text: formatMeta(Midi.meta),
            fill: ['0xffffff'],
            dropShadow: false,
            align: 'left',
            dropShadowBlur: 0
        });
        midiDeviceName.position.set(WIDTH / 2, 270);
        this.midiDeviceName = midiDeviceName;
        this.addChild(midiDeviceName);


        const midiKeysStatusString = createText({
            text: this.getMidiKeysStatusString(),
            fill: ['0xffffff'],
            dropShadow: false,
            align: 'left',
            dropShadowBlur: 0
        });
        midiKeysStatusString.position.set(WIDTH / 2, 590);
        this.midiKeysStatusString = midiKeysStatusString;
        this.addChild(midiKeysStatusString);

        EVENT_BUS.on('MIDI_onStateChange', (meta) => {
            this.midiDeviceName.text = formatMeta(meta);
        });

        this.initPiano();

        EVENT_BUS.on('MIDI_noteOn', ({ pianoNote }) => {
            if (pianoNote < this.minKey) {
                this.minKey = pianoNote;
                this.initPiano();
            } else if (pianoNote > this.maxKey) {
                this.maxKey = pianoNote;
                this.initPiano();
            }
            this.pixiPiano.highlightNote(pianoNote);
        });
        EVENT_BUS.on('MIDI_noteOff', ({ pianoNote }) => {
            this.pixiPiano.highlightNote(pianoNote, false);
        });
    }

    createVirtualPianoSection() {
        initVirtualKeyboard();
        const LABEL = 'No midi device found! \n';
        const MESSAGE = 'You can play the game using your keyboard\n(not recommended), controls schema is shown below.\n\nIf you forgot to connect the keyboard \n(or it was exclusively captured by DAW): connect it\n(close other apps) and hit refresh. Press "Continue" to play \nwith ASCI keyboard instead (virtual piano style)'
        this.minKey = 28;
        const label = createText({
            text: LABEL + MESSAGE,
            fill: ['0xffffff'],
            dropShadow: false,
            align: 'left',
            dropShadowBlur: 0
        });
        label.position.set(WIDTH / 2, 270);
        this.label = label;
        this.addChild(label);

        const midiKeysStatusString = createText({
            text: this.getMidiKeysStatusString(),
            fill: ['0xffffff'],
            dropShadow: false,
            align: 'left',
            dropShadowBlur: 0
        });
        midiKeysStatusString.position.set(WIDTH / 2, 550);
        this.midiKeysStatusString = midiKeysStatusString;
        this.addChild(midiKeysStatusString);

        this.initPiano({
            keyWidth: 23,
            showKeyLabels: true,
            overrideKeyLabels: VIRTUAL_KEYBOARD_SYMBOLS
        });
        EVENT_BUS.on('MIDI_noteOn', ({ pianoNote }) => {
            this.pixiPiano.highlightNote(pianoNote);
        });
        EVENT_BUS.on('MIDI_noteOff', ({ pianoNote }) => {
            this.pixiPiano.highlightNote(pianoNote, false);
        });
    }

    initPiano(config = {}) {
        if (this.pixiPiano) {
            this.removeChild(this.pixiPiano);
        }
        const pixiPiano = new PixiPiano({
            WHITE_KEY_COLOR: store.settings.whiteGemColor,
            BLACK_KEY_COLOR: store.settings.blackGemColor,
            boundaries: { min: this.minKey, max: this.maxKey },
            keyWidth: 14,
            PIANO_WIDTH: 540,
            ...config
        });
        pixiPiano.position.set(
            WIDTH / 2 - pixiPiano.width / 2,
            HEIGHT / 2 - pixiPiano.height / 2
        );

        this.addChild(pixiPiano);
        this.pixiPiano = pixiPiano;
        this.midiKeysStatusString.text = this.getMidiKeysStatusString();
        bounce(this.midiKeysStatusString);
        bounce(this.pixiPiano);
        const LABEL = 'Your Midi device was recognized as: \n';
        const MESSAGE = `Please press left- and right-most keys of your piano,\nto set-up the max range.\nCurrent range is: ${PianoUtil.note(this.minKey).toUpperCase()}...${PianoUtil.note(this.maxKey).toUpperCase()} (${this.maxKey - this.minKey} keys)\n\nOnce the keyboard range looks like yours press "Continue".\n"Refresh" will start it all over, if something didn't go right`
        function formatMeta(meta) {
            return LABEL + `${meta.manufacturer}-${meta.name} (${meta.state === 'connected' ? '✅' : '⛔'})\n\n` + MESSAGE;
        }
        if (this.midiDeviceName) {
            this.midiDeviceName.text = formatMeta(Midi.meta);
            store.updateSettings({
                pianoConfig: {
                    title: `${Midi.meta.manufacturer}-${Midi.meta.name}`,
                    boundaries: {
                        min: this.minKey,
                        max: this.maxKey
                    }
                }
            });
        } else {
            store.updateSettings({
                pianoConfig: {
                    title: `Virtual piano`,
                    showKeyLabels: true,
                    overrideKeyLabels: VIRTUAL_KEYBOARD_SYMBOLS,
                    boundaries: {
                        min: 28,
                        max: 64
                    }
                }
            });
        }
        this.pixiPiano.highlightNote(this.minKey);
        this.pixiPiano.highlightNote(this.maxKey);
    }

    getMidiKeysStatusString() {
        return `Key numbers are ${this.minKey}...${this.maxKey} (${this.maxKey - this.minKey} keys in total)`;
    }

}